import { DateTime } from "luxon";
import { classes } from "polytype";
import type {
  InvoiceBankingInformation,
  InvoicePaymentSettings,
  InvoiceSettingsData,
  InvoiceSettingsStatus,
  InvoiceSettingsUpdateRequestData,
} from "./types.js";
import { DataModel, ReferenceModel } from "../../base/index.js";
import type { AsyncResourceVariant } from "../../react/index.js";
import { provideReact } from "../../react/index.js";
import { config } from "../../config/config.js";
import assertObjectFound from "../../base/assertObjectFound.js";
import type { InvoiceRecipientData } from "../../invoice/index.js";
import { InvoiceRecipient } from "../../invoice/index.js";
import { language } from "../../base/i18n/setup.js";

export class InvoiceSettings extends ReferenceModel {
  public static ofCustomerId(customerId: string) {
    return new InvoiceSettings(customerId);
  }

  public static find = provideReact(async (id: string) => {
    const data = await config.behaviors.invoiceSettings.find(id);
    if (data) {
      return new InvoiceSettingsDetailed(data);
    }
  });

  public static get = provideReact(async (id: string) => {
    const invoiceSettings = await this.find(id);
    assertObjectFound(invoiceSettings, "invoiceSettings", id);
    return invoiceSettings;
  });

  public findDetailed = provideReact(
    () => InvoiceSettings.find(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<InvoiceSettingsDetailed | undefined>>;

  public getDetailed = provideReact(
    () => InvoiceSettings.get(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<InvoiceSettingsDetailed>>;

  public async update(data: InvoiceSettingsUpdateRequestData) {
    return await config.behaviors.invoiceSettings.update(this.id, data);
  }
}

class InvoiceSettingsCommon extends classes(
  DataModel<InvoiceSettingsData>,
  InvoiceSettings,
) {
  public readonly id: string;
  public readonly recipient?: InvoiceRecipient;
  public readonly status?: InvoiceSettingsStatus[];
  public readonly invoicePeriod: number;
  public readonly paymentSettings: InvoicePaymentSettings;
  public readonly additionalEmailRecipients: string[];
  public readonly lastBankingInformation?: InvoiceBankingInformation;
  public readonly printedInvoices?: boolean;
  public readonly recipientSameAsOwner?: boolean;
  public readonly targetDay?: number;
  public readonly translatedInvoicePeriod?: string;
  public readonly translatedPaymentMethod?: string;
  public readonly debitPaymentStopUntil?: DateTime;

  public constructor(data: InvoiceSettingsData) {
    super([data], [data.id]);
    this.id = data.id;
    if (data.recipient) {
      this.recipient = new InvoiceRecipient(data.recipient);
    }
    this.status = data.status;
    this.invoicePeriod = data.invoicePeriod ?? 1;
    this.paymentSettings = data.paymentSettings ?? { method: "invoice" };
    this.additionalEmailRecipients = data.additionalEmailRecipients ?? [];
    this.lastBankingInformation = data.lastBankingInformation;
    this.printedInvoices = data.printedInvoices;
    this.recipientSameAsOwner = data.recipientSameAsOwner;
    this.targetDay = data.targetDay;
    this.translatedInvoicePeriod = data.invoicePeriod
      ? language.default.invoiceSettings.invoicePeriod[
          (data.invoicePeriod as 1 | 3 | 6 | 12 | undefined) || 12
        ]
      : undefined;
    this.translatedPaymentMethod = data.paymentSettings?.method
      ? language.default.invoiceSettings.paymentMethod[
          data.paymentSettings.method
        ]
      : undefined;
    if (data.debitPaymentStopUntil) {
      this.debitPaymentStopUntil = DateTime.fromISO(data.debitPaymentStopUntil);
    }
  }

  public async addAdditionalRecipient(mailAddress: string) {
    return await this.update({
      invoicePeriod: this.invoicePeriod,
      recipient: this.data.recipient as InvoiceRecipientData,
      additionalEmailRecipients: [
        ...this.additionalEmailRecipients,
        mailAddress,
      ],
      paymentSettings: this.paymentSettings,
      recipientSameAsOwner: this.recipientSameAsOwner,
      printedInvoices: this.printedInvoices,
    });
  }

  public async removeAdditionalRecipient(mailAddress: string) {
    await this.update({
      invoicePeriod: this.invoicePeriod,
      recipient: this.data.recipient as InvoiceRecipientData,
      additionalEmailRecipients: this.additionalEmailRecipients.filter(
        (a) => a !== mailAddress,
      ),
      paymentSettings: this.paymentSettings,
      recipientSameAsOwner: this.recipientSameAsOwner,
      printedInvoices: this.printedInvoices,
    });
  }

  public async updatePaymentSettings(
    paymentSettings: InvoicePaymentSettings,
    invoicePeriod: number,
  ) {
    return await this.update({
      invoicePeriod,
      recipient: this.data.recipient as InvoiceRecipientData,
      additionalEmailRecipients: this.additionalEmailRecipients,
      paymentSettings:
        paymentSettings.method === "invoice"
          ? { method: paymentSettings.method }
          : paymentSettings,
      recipientSameAsOwner: this.recipientSameAsOwner,
      printedInvoices: this.printedInvoices,
    });
  }

  public async updateRecipient(
    recipient: InvoiceRecipientData,
    recipientSameAsOwner: boolean,
  ) {
    return await this.update({
      invoicePeriod: this.invoicePeriod,
      recipient,
      additionalEmailRecipients: this.additionalEmailRecipients,
      paymentSettings: this.paymentSettings,
      recipientSameAsOwner,
      printedInvoices: this.printedInvoices,
    });
  }
}

export class InvoiceSettingsDetailed extends classes(
  InvoiceSettingsCommon,
  DataModel<InvoiceSettingsData>,
) {
  public constructor(data: InvoiceSettingsData) {
    super([data], [data]);
  }
}
